import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import api from '../../services/api';
import ordersService from '../../services/ordersService';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TruckIcon, EnvelopeIcon, CheckCircleIcon, ClockIcon, PlusIcon } from '@heroicons/react/24/outline';
import { OrderDetailsModal } from '../../components/transport/OrderDetailsModal';
import { SendRfqModal } from '../../components/transport/SendRfqModal';
import { CreateTransportModal } from '../../components/transport/CreateTransportModal';
import { TransportDetailsModal } from '../../components/transport/TransportDetailsModal';
import { MessageModal } from '../../components/transport/MessageModal';
import { RfqPreviewModal } from '../../components/transport/RfqPreviewModal';
import { TimelineModal } from '../../components/transport/TimelineModal';
import { ClientConversationModal } from '../../components/transport/ClientConversationModal';
import { OfferGeneratorModal } from '../../components/transport/OfferGeneratorModal';
import type { OrderRecord } from '../../types/orders';

type Carrier = { id: number; name: string; email?: string; is_active?: boolean };
type Package = {
  // Frontend-friendly names (after getter conversion from backend)
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  // Also support raw DB names (before getter conversion)
  length_mm?: number;
  width_mm?: number;
  height_mm?: number;
  weight_kg?: number;
  quantity?: number;
  package_type?: string;
};

// Carrier invitation stored in JSON
type CarrierInvitation = {
  carrier_id: number;
  carrier_name: string;
  recipient_emails: {
    main_email?: string;
    forwarder_emails?: Array<{name: string; email: string}>;
    driver_emails?: Array<{name: string; email: string}>;
  };
  status?: string;
  offer_price?: string;
  offer_currency?: string;
  vehicle_type?: string;
  delivery_eta?: string;
  notes?: string;
  responded_at?: string;
  selected?: boolean;
};

// Order stored in JSON
type OrderInTransport = {
  order_id: number;
  order_number: string;
  ref_name?: string;
  order_date?: string;
  responsible?: string;
  estimated_delivery_date?: string;
  product_short_description?: string;
};

type Transport = {
  id: number;
  reference?: string;
  status: string;
  readiness_date?: string;
  client_name?: string;
  client_surname?: string;
  client_company?: string;
  client_nip?: string;
  client_email?: string;
  delivery_address?: string;
  notes?: string;
  carrier_invitations?: CarrierInvitation[];  // JSON column
  orders?: OrderInTransport[];                 // JSON column
  packages?: Package[];                        // JSON column
  created_at?: string;
  created_by?: number;
};
type Message = {
  id: number;
  transport_id?: number;
  direction: 'outgoing' | 'incoming';
  sender_email?: string;
  recipient_email?: string;
  subject?: string;
  body?: string;
  sent_at?: string;
  created_at?: string;
  invitation?: { carrier?: Carrier };
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  rfq_sent: 'text-white border-[#016ba3]',
  offers_received: 'bg-orange-100 text-orange-700 border-orange-300',
  offer_selected: 'bg-green-100 text-green-700 border-green-300',
  booked: 'bg-purple-100 text-purple-700 border-purple-300',
  delivered: 'text-white border-green-700',
  closed: 'bg-gray-200 text-gray-600 border-gray-400'
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Przygotowany',
  rfq_sent: 'Zapytania wysłane',
  offers_received: 'Otrzymano oferty',
  offer_selected: 'Wybrano ofertę',
  booked: 'Zarezerwowano',
  delivered: 'Dostarczono',
  closed: 'Zamknięty'
};

export default function Transports() {
  const { t } = useTranslation();
  const [transports, setTransports] = useState<Transport[]>([]);
  const [activeTransports, setActiveTransports] = useState<Transport[]>([]);
  const [loading, setLoading] = useState(true);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [orderNumberFilter, setOrderNumberFilter] = useState<string>('');
  const [readyOrdersFilter, setReadyOrdersFilter] = useState<string>('');

  // Ready orders state
  const [readyOrders, setReadyOrders] = useState<OrderRecord[]>([]);
  const [selectedReadyOrders, setSelectedReadyOrders] = useState<number[]>([]);
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());

  // Pagination state
  const [readyOrdersPage, setReadyOrdersPage] = useState(1);
  const [transportsPage, setTransportsPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  // Modal states
  const [orderDetailsModal, setOrderDetailsModal] = useState<{ open: boolean; transportId: number | null }>({ open: false, transportId: null });
  const [selectedOrders, setSelectedOrders] = useState<OrderRecord[]>([]);
  const [transportDetailsModal, setTransportDetailsModal] = useState<{ open: boolean; transport: Transport | null }>({ open: false, transport: null });
  const [rfqModal, setRfqModal] = useState<{ open: boolean; transport: Transport | null }>({ open: false, transport: null });
  const [createTransportModal, setCreateTransportModal] = useState(false);
  const [messagesMap, setMessagesMap] = useState<Record<number, Message[]>>({});
  const [expandedTimeline, setExpandedTimeline] = useState<Record<number, boolean>>({});
  const [messageModal, setMessageModal] = useState<{ open: boolean; message: Message | null }>({ open: false, message: null });
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [checkingEmails, setCheckingEmails] = useState(false);
  const [rfqPreviewModal, setRfqPreviewModal] = useState<{ open: boolean; transportId: number | null }>({ open: false, transportId: null });
  const [timelineModal, setTimelineModal] = useState<{ open: boolean; transport: Transport | null }>({ open: false, transport: null });
  const [clientConversationModal, setClientConversationModal] = useState<{ open: boolean; transport: Transport | null }>({ open: false, transport: null });
  const [offerGeneratorModal, setOfferGeneratorModal] = useState<{ open: boolean; transport: Transport | null }>({ open: false, transport: null });
  const [expandedPackages, setExpandedPackages] = useState<Set<number>>(new Set());

  const loadTransports = async () => {
    try {
      setLoading(true);
      const resp = await api.get('/transport');
      const allTransports = resp.data.data || resp.data || [];

      // Separate draft and active transports (archived moved to separate module)
      const drafts = allTransports.filter((t: Transport) => t.status === 'draft');
      const actives = allTransports.filter((t: Transport) => t.status !== 'draft' && t.status !== 'closed' && t.status !== 'delivered');

      setTransports(drafts);
      setActiveTransports(actives);
    } catch (e: any) {
      toast.error(e?.message || 'Błąd ładowania zleceń');
    } finally {
      setLoading(false);
    }
  };

  const loadReadyOrders = async () => {
    try {
      // Pobierz zamówienia z has_transport=true AND has_ordered=false z dedykowanego endpointu
      // Backend już filtruje przez has_ordered, więc nie potrzeba dodatkowego filtrowania frontendowego
      const ordersWithTransport = await ordersService.getReadyForTransport();
      setReadyOrders(ordersWithTransport);
    } catch (e) {
      console.error('Failed to load ready orders', e);
    }
  };

  const loadCarriers = async () => {
    try {
      const resp = await api.get('/transport/carriers/list');
      setCarriers(resp.data || []);
    } catch (e) {
      console.error('Failed to load carriers', e);
    }
  };

  const loadMessages = async (transportId: number) => {
    try {
      const resp = await api.get(`/transport/${transportId}/messages`);
      setMessagesMap(prev => ({ ...prev, [transportId]: resp.data || [] }));
    } catch (e) {
      console.error('Failed to load messages', e);
    }
  };

  const openOrderDetails = async (transportId: number) => {
    try {
      const resp = await api.get(`/transport/${transportId}/orders`);
      setSelectedOrders(resp.data || []);
      setOrderDetailsModal({ open: true, transportId });
    } catch (e: any) {
      toast.error('Nie udało się załadować szczegółów zamówień');
    }
  };

  const openTransportDetails = async (transportId: number) => {
    try {
      // Pobierz pełne dane transportu z zamówieniami, przewoźnikami i paczkami
      const resp = await api.get(`/transport/${transportId}`);
      setTransportDetailsModal({ open: true, transport: resp.data });
    } catch (e: any) {
      toast.error('Nie udało się załadować szczegółów zlecenia');
    }
  };

  const openRfqModal = (transport: Transport) => {
    setRfqModal({ open: true, transport });
  };

  const deleteTransport = async (transportId: number) => {
    if (!confirm('Czy na pewno chcesz usunąć to zlecenie transportowe? Zamówienia zostaną zwrócone do listy gotowych do transportu.')) return;
    try {
      await api.delete(`/transport/${transportId}`);
      toast.success('Zlecenie transportowe zostało usunięte');
      await loadTransports();
      await loadReadyOrders(); // Odśwież listę gotowych zamówień
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Nie udało się usunąć zlecenia');
    }
  };

  const forceDeleteTransport = async (transportId: number) => {
    if (!confirm('UWAGA: Czy na pewno chcesz usunąć to aktywne zlecenie transportowe? Wszystkie wiadomości i historia zostaną usunięte. Zamówienia zostaną zwrócone do listy gotowych do transportu.')) return;
    try {
      await api.delete(`/transport/${transportId}/force`);
      toast.success('Zlecenie transportowe zostało usunięte');
      await loadTransports();
      await loadReadyOrders(); // Odśwież listę gotowych zamówień
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Nie udało się usunąć zlecenia');
    }
  };

  const markInTransport = async (transportId: number) => {
    if (!confirm('Czy na pewno chcesz oznaczyć to zlecenie jako w transporcie?')) return;
    try {
      await api.patch(`/transport/${transportId}/mark-in-transport`);
      toast.success('Zlecenie oznaczone jako w transporcie');
      await loadTransports();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Nie udało się oznaczyć zlecenia');
    }
  };

  const completeTransport = async (transportId: number) => {
    if (!confirm('Czy na pewno chcesz oznaczyć to zlecenie jako zakończone?')) return;
    try {
      await api.patch(`/transport/${transportId}/complete`);
      toast.success('Zlecenie oznaczone jako zakończone');
      await loadTransports();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Nie udało się zakończyć zlecenia');
    }
  };

  const checkNewEmails = async () => {
    try {
      setCheckingEmails(true);
      const response = await api.post('/transport/check-emails', {});
      toast.success(`Znaleziono ${response.data.newMessages} nowych wiadomości`);

      // Reload messages for all expanded transports
      for (const transportId of Object.keys(expandedTimeline)) {
        if (expandedTimeline[parseInt(transportId)]) {
          await loadMessages(parseInt(transportId));
        }
      }
    } catch (e: any) {
      toast.error('Nie udało się sprawdzić emaili');
    } finally {
      setCheckingEmails(false);
    }
  };

  const loadConnectionStatus = async () => {
    try {
      const response = await api.get('/transport/connection-status');
      setConnectionStatus(response.data);
    } catch (e) {
      console.error('Failed to load connection status:', e);
    }
  };

  const openMessageModal = (message: Message) => {
    setMessageModal({ open: true, message });
  };

  const selectOffer = async (transportId: number, carrierIndex: number) => {
    if (!confirm('Czy na pewno chcesz zaakceptować tę ofertę?')) return;
    try {
      await api.post(`/transport/${transportId}/select-offer`, { carrier_index: carrierIndex });
      toast.success('Oferta zaakceptowana');
      await loadTransports();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Nie udało się zaakceptować oferty');
    }
  };

  const toggleTimeline = async (transportId: number) => {
    const isExpanded = expandedTimeline[transportId];
    if (!isExpanded) {
      await loadMessages(transportId);
    }
    setExpandedTimeline(prev => ({ ...prev, [transportId]: !isExpanded }));
  };

  // Funkcje dla sekcji gotowych zamówień
  const toggleReadyOrderSelection = (orderId: number) => {
    setSelectedReadyOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const toggleAllReadyOrders = () => {
    if (selectedReadyOrders.length === readyOrders.length) {
      setSelectedReadyOrders([]);
    } else {
      setSelectedReadyOrders(readyOrders.map(o => o.id));
    }
  };

  const createTransportFromOrders = () => {
    if (selectedReadyOrders.length === 0) {
      toast.error('Wybierz co najmniej jedno zamówienie');
      return;
    }

    // Otwórz modal do tworzenia transportu
    setCreateTransportModal(true);
  };

  const handleTransportCreated = async () => {
    // Odśwież listy po utworzeniu transportu
    await loadReadyOrders();
    await loadTransports();

    // Wyczyść zaznaczenie
    setSelectedReadyOrders([]);
  };

  const toggleProductsExpand = (orderId: number) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    loadReadyOrders();
    loadTransports();
    loadCarriers();
    loadConnectionStatus();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setReadyOrdersPage(1);
  }, [readyOrdersFilter]);

  useEffect(() => {
    setTransportsPage(1);
  }, [orderNumberFilter]);

  // Filter functions
  const filterByOrderNumber = (transportsList: Transport[]) => {
    if (!orderNumberFilter.trim()) return transportsList;

    const searchTerm = orderNumberFilter.toLowerCase().trim();
    return transportsList.filter(transport => {
      if (!transport.orders || transport.orders.length === 0) return false;
      return transport.orders.some(order =>
        order.order_nr?.toLowerCase().includes(searchTerm)
      );
    });
  };

  const filterReadyOrders = (ordersList: OrderRecord[]) => {
    if (!readyOrdersFilter.trim()) return ordersList;

    const searchTerm = readyOrdersFilter.toLowerCase().trim();
    return ordersList.filter(order =>
      order.order_nr?.toLowerCase().includes(searchTerm) ||
      order.ref_name?.toLowerCase().includes(searchTerm)
    );
  };

  const filteredTransports = filterByOrderNumber(transports);
  const filteredActiveTransports = filterByOrderNumber(activeTransports);
  const filteredReadyOrders = filterReadyOrders(readyOrders);

  // Pagination calculations
  const totalReadyOrdersPages = Math.ceil(filteredReadyOrders.length / ITEMS_PER_PAGE);
  const totalTransportsPages = Math.ceil(filteredTransports.length / ITEMS_PER_PAGE);

  const paginatedReadyOrders = filteredReadyOrders.slice(
    (readyOrdersPage - 1) * ITEMS_PER_PAGE,
    readyOrdersPage * ITEMS_PER_PAGE
  );

  const paginatedTransports = filteredTransports.slice(
    (transportsPage - 1) * ITEMS_PER_PAGE,
    transportsPage * ITEMS_PER_PAGE
  );

  if (loading) return <div className="p-6">Ładowanie...</div>;

  return (
    <div className="p-6 space-y-6">

      {/* SEKCJA 0: Zamówienia gotowe do transportu */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Zamówienia gotowe do transportu ({readyOrders.length})</CardTitle>
              <p className="text-sm text-gray-500 mt-1">Kliknij na zamówienie, aby je zaznaczyć</p>
            </div>
            <div className="flex items-center gap-2">
              {readyOrders.length > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleAllReadyOrders}
                  >
                    {selectedReadyOrders.length === readyOrders.length ? 'Odznacz wszystko' : 'Zaznacz wszystko'}
                  </Button>
                  <Button
                    onClick={createTransportFromOrders}
                    disabled={selectedReadyOrders.length === 0}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Utwórz zlecenie transportowe ({selectedReadyOrders.length})
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtr dla zamówień gotowych */}
          {readyOrders.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={readyOrdersFilter}
                    onChange={(e) => setReadyOrdersFilter(e.target.value)}
                    placeholder="Filtruj po numerze zamówienia lub nazwie ref..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {readyOrdersFilter && (
                  <button
                    onClick={() => setReadyOrdersFilter('')}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Wyczyść filtr
                  </button>
                )}
              </div>
            </div>
          )}

          {filteredReadyOrders.length === 0 && readyOrders.length > 0 ? (
            <div className="text-center py-8 text-gray-500">
              Brak zamówień pasujących do "{readyOrdersFilter}"
            </div>
          ) : readyOrders.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Brak zamówień gotowych do transportu. Przekaż zamówienia z modułu "Zamówienia" używając przycisku "Przekaż do zleceń transportowych".
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedReadyOrders.map(order => {
                const isSelected = selectedReadyOrders.includes(order.id);
                const isExpanded = expandedProducts.has(order.id);
                // Split products by comma or semicolon
                const products = order.product_short_description
                  ? order.product_short_description.split(/[,;]/).map(p => p.trim()).filter(Boolean)
                  : [];
                const PRODUCTS_PREVIEW_LIMIT = 3;
                const displayedProducts = isExpanded ? products : products.slice(0, PRODUCTS_PREVIEW_LIMIT);
                const hasMoreProducts = products.length > PRODUCTS_PREVIEW_LIMIT;

                return (
                  <div
                    key={order.id}
                    onClick={() => toggleReadyOrderSelection(order.id)}
                    className={`group relative rounded-2xl p-6 cursor-pointer transition-all duration-300 border ${
                      isSelected
                        ? 'bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 border-blue-400 shadow-2xl ring-4 ring-blue-500/30 scale-[1.02]'
                        : 'bg-gradient-to-br from-white via-gray-50 to-gray-100 border-gray-200 hover:border-blue-300 hover:shadow-xl hover:-translate-y-1'
                    }`}
                  >
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-lg animate-pulse z-10">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}

                    {/* Numer zamówienia */}
                    <div className="mb-5 pb-4 border-b border-gray-200">
                      <div className="relative bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl px-5 py-4 shadow-md overflow-hidden">
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute inset-0" style={{
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                            backgroundSize: '24px 24px'
                          }}></div>
                        </div>

                        <div className="relative flex items-center justify-center gap-2 mb-1">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          <div className="text-xl font-bold text-white">{order.order_nr || 'Brak numeru'}</div>
                        </div>
                        <div className="relative flex items-center justify-center gap-1.5 text-xs text-blue-50 uppercase tracking-wider font-semibold">
                          <div className="w-1.5 h-1.5 bg-blue-300 rounded-full animate-pulse"></div>
                          Numer zamówienia
                        </div>
                      </div>
                    </div>

                    {/* Szczegóły zamówienia */}
                    <div className="space-y-3 mb-5 pb-4 border-b border-dashed border-gray-300">
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center group-hover/item:bg-emerald-100 transition-colors">
                          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-500 font-medium mb-1">Nazwa referencyjna</div>
                          <div className="text-sm font-semibold text-gray-800 break-words">{order.ref_name || 'Brak nazwy'}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center group-hover/item:bg-purple-100 transition-colors">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-1">Data zamówienia</div>
                          <div className="text-sm text-gray-700 font-medium">{order.order_date || 'Brak daty'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Produkty jako kafelki */}
                    {products.length > 0 && (
                      <div className="mb-5 pb-4 border-b border-dashed border-gray-300">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0 w-7 h-7 bg-rose-50 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                            <div className="text-xs text-gray-600 uppercase tracking-wide font-semibold">Produkty ({products.length})</div>
                          </div>
                          {hasMoreProducts && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleProductsExpand(order.id);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                              {isExpanded ? (
                                <>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                  Zwiń
                                </>
                              ) : (
                                <>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                  Rozwiń ({products.length - PRODUCTS_PREVIEW_LIMIT})
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {displayedProducts.map((product, idx) => {
                            const isExtraProduct = idx >= PRODUCTS_PREVIEW_LIMIT;
                            return (
                              <span
                                key={idx}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 text-rose-900 shadow-sm hover:shadow-md transition-all duration-300 ${
                                  isExtraProduct && isExpanded
                                    ? 'animate-slideIn'
                                    : isExtraProduct
                                    ? 'hidden'
                                    : ''
                                }`}
                                style={{
                                  animationDelay: isExtraProduct ? `${(idx - PRODUCTS_PREVIEW_LIMIT) * 50}ms` : '0ms'
                                }}
                              >
                                <svg className="w-3 h-3 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                {product}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Dodatkowe informacje */}
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center group-hover/item:bg-teal-100 transition-colors">
                          <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-1">Odpowiedzialny</div>
                          <div className="text-sm font-medium text-gray-800">{order.responsible || '-'}</div>
                        </div>
                      </div>
                      {order.estimated_delivery_date && (
                        <div className="flex items-start gap-3 group/item">
                          <div className="flex-shrink-0 w-8 h-8 bg-sky-50 rounded-lg flex items-center justify-center group-hover/item:bg-sky-100 transition-colors">
                            <svg className="w-4 h-4 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="text-xs text-gray-500 font-medium mb-1">Szacowana dostawa</div>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200 rounded-lg text-sm font-semibold text-sky-900">
                              {order.estimated_delivery_date}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              </div>

              {/* Pagination dla zamówień gotowych - zawsze widoczny */}
              <div className="flex flex-col items-center gap-3 mt-6">
                <div className="text-sm text-gray-600">
                  Wyświetlanie {((readyOrdersPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(readyOrdersPage * ITEMS_PER_PAGE, filteredReadyOrders.length)} z {filteredReadyOrders.length} zamówień
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setReadyOrdersPage(prev => Math.max(1, prev - 1))}
                    disabled={readyOrdersPage === 1 || totalReadyOrdersPages <= 1}
                    className="px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                  >
                    ← Poprzednia
                  </button>

                  <div className="flex gap-1">
                    {Array.from({ length: Math.max(1, totalReadyOrdersPages) }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setReadyOrdersPage(page)}
                        disabled={totalReadyOrdersPages <= 1}
                        className={`px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:cursor-not-allowed ${
                          readyOrdersPage === page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'hover:bg-gray-100 disabled:hover:bg-white'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setReadyOrdersPage(prev => Math.min(totalReadyOrdersPages, prev + 1))}
                    disabled={readyOrdersPage === totalReadyOrdersPages || totalReadyOrdersPages <= 1}
                    className="px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                  >
                    Następna →
                  </button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* SEKCJA 1: Zlecenia DRAFT */}
      <Card>
        <CardHeader>
          <CardTitle>Przygotowane zlecenia transportowe</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Info box */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-blue-900 font-medium mb-1">
                  Gotowe zamówienia zebrane w zlecenie transportowe
                </p>
                <p className="text-sm text-blue-700">
                  Aby zmienić status na aktywne, wyślij zapytania o zlecenie transportowe do przewoźników.
                </p>
              </div>
            </div>
          </div>

          {/* Filtr dla przygotowanych zleceń */}
          <div className="mb-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={orderNumberFilter}
                  onChange={(e) => setOrderNumberFilter(e.target.value)}
                  placeholder="Filtruj po numerze zamówienia..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {orderNumberFilter && (
                <button
                  onClick={() => setOrderNumberFilter('')}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Wyczyść filtr
                </button>
              )}
            </div>
          </div>

          {filteredTransports.length === 0 && transports.length > 0 ? (
            <div className="text-center py-8 text-gray-500">
              Brak zleceń pasujących do "{orderNumberFilter}"
            </div>
          ) : filteredTransports.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Brak przygotowanych zleceń. Dodaj zlecenie z listy zamówień.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedTransports.map(transport => {
                // Get packages directly from transport (not from orders)
                const allPackages: Package[] = transport.packages || [];

                return (
                  <div
                    key={transport.id}
                    className="group relative bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:ring-2 hover:ring-green-500/20"
                  >
                    {/* Numer zlecenia */}
                    <div className="mb-5 pb-4 border-b border-gray-200">
                      <div className="relative bg-gradient-to-br from-green-500 to-green-700 rounded-xl px-5 py-4 shadow-md overflow-hidden">
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute inset-0" style={{
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                            backgroundSize: '24px 24px'
                          }}></div>
                        </div>

                        <div className="relative flex items-center justify-center gap-2 mb-1">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div className="text-xl font-bold text-white">Zlecenie #{transport.id}</div>
                        </div>
                        <div className="relative flex items-center justify-center gap-1.5 text-xs text-green-50 uppercase tracking-wider font-semibold">
                          <div className="w-1.5 h-1.5 bg-green-300 rounded-full animate-pulse"></div>
                          {STATUS_LABELS[transport.status] || transport.status}
                        </div>
                      </div>
                    </div>

                    {/* Szczegóły zlecenia */}
                    <div className="space-y-3 mb-5 pb-4 border-b border-dashed border-gray-300">
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center group-hover/item:bg-blue-100 transition-colors">
                          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-500 font-medium mb-1">Numery zamówień</div>
                          <div className="text-sm font-semibold text-gray-800 break-words">
                            {transport.orders && transport.orders.length > 0 ? transport.orders.map(o => o.order_number).filter(Boolean).join(', ') : 'Brak numeru'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center group-hover/item:bg-purple-100 transition-colors">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-1">Data gotowości</div>
                          <div className="text-sm text-gray-700 font-medium">{transport.readiness_date || 'Brak daty'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Dodatkowe informacje */}
                    <div className="space-y-3 mb-5 pb-4 border-b border-dashed border-gray-300">
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center group-hover/item:bg-amber-100 transition-colors">
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-1.5">Wymiar ładowny</div>
                          <div className="text-sm font-medium text-gray-800">
                            {allPackages.length > 0 ? (
                              <div className="space-y-1.5">
                                {allPackages.map((pkg, idx) => (
                                  <div key={idx} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg text-xs font-semibold text-amber-900">
                                    <svg className="w-3 h-3 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                    {pkg.length || pkg.length_mm || 0}×{pkg.width || pkg.width_mm || 0}×{pkg.height || pkg.height_mm || 0}mm, {pkg.weight || pkg.weight_kg || 0}kg
                                    {pkg.quantity && pkg.quantity > 1 && ` (×${pkg.quantity})`}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">Brak danych</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center group-hover/item:bg-indigo-100 transition-colors">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-1">Przewoźnicy</div>
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg text-sm font-semibold text-indigo-900">
                            {transport.carrier_invitations ? transport.carrier_invitations.length : 0} wybranych
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Przyciski */}
                    <div className="flex flex-col gap-2.5">
                      <Button
                        size="sm"
                        onClick={() => openTransportDetails(transport.id)}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Szczegóły i edycja
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openRfqModal(transport)}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        disabled={!transport.carrier_invitations || transport.carrier_invitations.length === 0}
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-2" />
                        Wyślij zapytania
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => deleteTransport(transport.id)}
                        className="w-full bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Usuń
                      </Button>
                    </div>
                  </div>
                );
              })}
              </div>

              {/* Pagination dla przygotowanych zleceń - zawsze widoczny */}
              <div className="flex flex-col items-center gap-3 mt-6">
                <div className="text-sm text-gray-600">
                  Wyświetlanie {((transportsPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(transportsPage * ITEMS_PER_PAGE, filteredTransports.length)} z {filteredTransports.length} zleceń
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setTransportsPage(prev => Math.max(1, prev - 1))}
                    disabled={transportsPage === 1 || totalTransportsPages <= 1}
                    className="px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                  >
                    ← Poprzednia
                  </button>

                  <div className="flex gap-1">
                    {Array.from({ length: Math.max(1, totalTransportsPages) }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setTransportsPage(page)}
                        disabled={totalTransportsPages <= 1}
                        className={`px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:cursor-not-allowed ${
                          transportsPage === page
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'hover:bg-gray-100 disabled:hover:bg-white'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setTransportsPage(prev => Math.min(totalTransportsPages, prev + 1))}
                    disabled={transportsPage === totalTransportsPages || totalTransportsPages <= 1}
                    className="px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                  >
                    Następna →
                  </button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* SEKCJA 2: Aktywne zlecenia transportowe */}
      <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Aktywne zlecenia transportowe ({filteredActiveTransports.length})</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Zlecenia w trakcie realizacji</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Connection status */}
                {connectionStatus && (
                  <div className="flex items-center gap-2 text-sm">
                    <div className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${connectionStatus.imap?.connected ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-gray-600">IMAP</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className={`w-2 h-2 rounded-full ${connectionStatus.smtp?.connected ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-gray-600">SMTP</span>
                    </div>
                  </div>
                )}
                {/* Refresh button */}
                <Button
                  size="sm"
                  onClick={checkNewEmails}
                  disabled={checkingEmails}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {checkingEmails ? 'Sprawdzanie...' : 'Odśwież wiadomości'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredActiveTransports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Brak aktywnych zleceń transportowych
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredActiveTransports.map(transport => {
                const isExpanded = expandedTimeline[transport.id];
                const messages = messagesMap[transport.id] || [];
                const carrierInvitations = transport.carrier_invitations || [];
                const respondedInvitations = carrierInvitations.filter(inv => inv.status === 'responded');

                // Get all packages
                const allPackages: Package[] = transport.packages || [];

                // Determine status colors
                const getStatusGradient = (status: string) => {
                  switch(status) {
                    case 'rfq_sent':
                      return 'from-cyan-500 to-blue-600';
                    case 'offers_received':
                      return 'from-orange-500 to-amber-600';
                    case 'booked':
                      return 'from-purple-500 to-indigo-600';
                    case 'delivered':
                      return 'from-emerald-500 to-green-600';
                    default:
                      return 'from-gray-500 to-gray-600';
                  }
                };

                return (
                  <div
                    key={transport.id}
                    className="group relative bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:ring-2 hover:ring-blue-500/20"
                  >
                    {/* Numer zlecenia z statusem */}
                    <div className="mb-5 pb-4 border-b border-gray-200">
                      <div className={`relative bg-gradient-to-br ${getStatusGradient(transport.status)} rounded-xl px-5 py-4 shadow-md overflow-hidden`}>
                        {/* Background pattern */}
                        <div className="absolute inset-0 opacity-10">
                          <div className="absolute inset-0" style={{
                            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                            backgroundSize: '24px 24px'
                          }}></div>
                        </div>

                        <div className="relative flex items-center justify-center gap-2 mb-1">
                          {respondedInvitations.length > 0 && (
                            <div className="absolute -top-2 -right-2 flex items-center justify-center w-8 h-8 bg-orange-500 text-white rounded-full text-xs font-bold shadow-lg animate-pulse">
                              {respondedInvitations.length}
                            </div>
                          )}
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                          </svg>
                          <div className="text-xl font-bold text-white">Zlecenie #{transport.id}</div>
                        </div>
                        <div className="relative flex items-center justify-center gap-1.5 text-xs text-white/90 uppercase tracking-wider font-semibold">
                          <div className="w-1.5 h-1.5 bg-white/80 rounded-full animate-pulse"></div>
                          {STATUS_LABELS[transport.status] || transport.status}
                        </div>
                      </div>
                    </div>

                    {/* Numery zamówień jako kafelki */}
                    {transport.orders && transport.orders.length > 0 && (
                      <div className="mb-5 pb-4 border-b border-dashed border-gray-300">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex-shrink-0 w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                          </div>
                          <div className="text-xs text-gray-600 uppercase tracking-wide font-semibold">Zamówienia ({transport.orders.length})</div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {transport.orders.map((order, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 text-blue-700 shadow-sm hover:shadow-md transition-shadow"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                              </svg>
                              {order.order_number}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Szczegóły zlecenia */}
                    <div className="space-y-3 mb-5 pb-4 border-b border-dashed border-gray-300">
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center group-hover/item:bg-purple-100 transition-colors">
                          <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-1">Data gotowości</div>
                          <div className="text-sm text-gray-700 font-medium">{transport.readiness_date || 'Brak daty'}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 group/item">
                        <div className="flex-shrink-0 w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center group-hover/item:bg-indigo-100 transition-colors">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="text-xs text-gray-500 font-medium mb-1">Przewoźnicy</div>
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg text-sm font-semibold text-indigo-900">
                            {carrierInvitations.length} wybranych
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Wymiary paczek jako kafelki */}
                    {allPackages.length > 0 && (
                      <div className="mb-5 pb-4 border-b border-dashed border-gray-300">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0 w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
                              <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                            </div>
                            <div className="text-xs text-gray-600 uppercase tracking-wide font-semibold">Paczki ({allPackages.length})</div>
                          </div>
                          {allPackages.length > 3 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const newSet = new Set(expandedPackages);
                                if (newSet.has(transport.id)) {
                                  newSet.delete(transport.id);
                                } else {
                                  newSet.add(transport.id);
                                }
                                setExpandedPackages(newSet);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                            >
                              {expandedPackages.has(transport.id) ? (
                                <>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                  Zwiń
                                </>
                              ) : (
                                <>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                  Rozwiń ({allPackages.length - 3})
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {(expandedPackages.has(transport.id) ? allPackages : allPackages.slice(0, 3)).map((pkg, idx) => {
                            const isExtraPackage = idx >= 3;
                            return (
                              <span
                                key={idx}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 text-amber-900 shadow-sm hover:shadow-md transition-all duration-300 ${
                                  isExtraPackage && expandedPackages.has(transport.id)
                                    ? 'animate-slideIn'
                                    : ''
                                }`}
                                style={{
                                  animationDelay: isExtraPackage ? `${(idx - 3) * 50}ms` : '0ms'
                                }}
                              >
                                <svg className="w-3 h-3 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                                {pkg.length || pkg.length_mm || 0}×{pkg.width || pkg.width_mm || 0}×{pkg.height || pkg.height_mm || 0}mm, {pkg.weight || pkg.weight_kg || 0}kg
                                {pkg.quantity && pkg.quantity > 1 && ` (×${pkg.quantity})`}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Przyciski akcji */}
                    <div className="flex flex-col gap-2.5">
                      <Button
                        size="sm"
                        onClick={async () => {
                          await loadMessages(transport.id);
                          setTimelineModal({ open: true, transport });
                        }}
                        className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 relative overflow-hidden group"
                      >
                        <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        <div className="relative flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <span>Konwersacja z przewoźnikiem</span>
                          {respondedInvitations.length > 0 && (
                            <span className="px-2 py-0.5 bg-orange-500 text-white rounded-full text-xs font-bold shadow-md animate-pulse">
                              {respondedInvitations.length} ofert
                            </span>
                          )}
                        </div>
                      </Button>
                      <Button
                        size="sm"
                        onClick={async () => {
                          await loadMessages(transport.id);
                          setClientConversationModal({ open: true, transport });
                        }}
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200 relative overflow-hidden group"
                      >
                        <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                        <div className="relative flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                          </svg>
                          <span>Konwersacja z klientem</span>
                        </div>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => markInTransport(transport.id)}
                        className="w-full bg-gradient-to-r from-orange-600 to-amber-700 hover:from-orange-700 hover:to-amber-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                        </svg>
                        W transporcie
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => completeTransport(transport.id)}
                        className="w-full bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Zakończone
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => forceDeleteTransport(transport.id)}
                        className="w-full bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-700 hover:to-rose-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Usuń
                      </Button>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </CardContent>
        </Card>

      {/* Modals */}
      <OrderDetailsModal
        isOpen={orderDetailsModal.open}
        onClose={() => {
          setOrderDetailsModal({ open: false, transportId: null });
          loadTransports(); // Refresh transports after closing modal
        }}
        orders={selectedOrders}
      />

      {rfqModal.transport && (
        <SendRfqModal
          isOpen={rfqModal.open}
          onClose={() => setRfqModal({ open: false, transport: null })}
          transportId={rfqModal.transport.id}
          carriers={
            rfqModal.transport.carrier_invitations?.map(inv => {
              // Get first available email from forwarder or driver emails
              const forwarderEmail = inv.recipient_emails?.forwarder_emails?.[0];
              const driverEmail = inv.recipient_emails?.driver_emails?.[0];
              const email = typeof forwarderEmail === 'object' ? forwarderEmail.email :
                           typeof driverEmail === 'object' ? driverEmail.email :
                           inv.recipient_emails?.main_email || '';

              return {
                id: inv.carrier_id,
                name: inv.carrier_name,
                email: email
              };
            }) || []
          }
          packages={rfqModal.transport.packages || []}
          productDescriptions={
            rfqModal.transport.orders?.map(order => order.product_short_description || 'Brak opisu').filter(Boolean) || []
          }
          onSuccess={loadTransports}
        />
      )}

      <CreateTransportModal
        isOpen={createTransportModal}
        onClose={() => setCreateTransportModal(false)}
        selectedOrderIds={selectedReadyOrders}
        selectedOrders={readyOrders.filter(order => selectedReadyOrders.includes(order.id))}
        onSuccess={handleTransportCreated}
      />

      <TransportDetailsModal
        isOpen={transportDetailsModal.open}
        onClose={() => setTransportDetailsModal({ open: false, transport: null })}
        transport={transportDetailsModal.transport}
      />

      <MessageModal
        isOpen={messageModal.open}
        onClose={() => setMessageModal({ open: false, message: null })}
        message={messageModal.message}
        onReplySent={() => {
          // Reload messages for the transport
          if (messageModal.message) {
            const transportId = messageModal.message.transport_id;
            if (transportId) {
              loadMessages(transportId);
            }
          }
        }}
      />

      <RfqPreviewModal
        isOpen={rfqPreviewModal.open}
        onClose={() => setRfqPreviewModal({ open: false, transportId: null })}
        transportId={rfqPreviewModal.transportId || 0}
      />

      <TimelineModal
        isOpen={timelineModal.open}
        onClose={() => setTimelineModal({ open: false, transport: null })}
        transport={timelineModal.transport}
        messages={timelineModal.transport ? messagesMap[timelineModal.transport.id] || [] : []}
        onRefresh={async () => {
          await loadTransports();
          if (timelineModal.transport) {
            await loadMessages(timelineModal.transport.id);
          }
        }}
      />

      <ClientConversationModal
        isOpen={clientConversationModal.open}
        onClose={() => setClientConversationModal({ open: false, transport: null })}
        transport={clientConversationModal.transport}
        onCreateOffer={async () => {
          // Load messages first before opening offer generator
          if (clientConversationModal.transport) {
            await loadMessages(clientConversationModal.transport.id);
          }
          // Close client conversation modal and open offer generator
          setOfferGeneratorModal({ open: true, transport: clientConversationModal.transport });
          setClientConversationModal({ open: false, transport: null });
        }}
      />

      <OfferGeneratorModal
        isOpen={offerGeneratorModal.open}
        onClose={() => setOfferGeneratorModal({ open: false, transport: null })}
        transport={offerGeneratorModal.transport}
        messages={offerGeneratorModal.transport ? messagesMap[offerGeneratorModal.transport.id] || [] : []}
        onSuccess={() => {
          // Refresh transports after sending offer
          loadTransports();
          if (offerGeneratorModal.transport) {
            loadMessages(offerGeneratorModal.transport.id);
          }
        }}
      />
    </div>
  );
}
